import {
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Select, type SingleValue } from 'chakra-react-select';
import { type Dispatch, type SetStateAction, useMemo } from 'react';
import NumberFormat, {
  type NumberFormatPropsBase,
  type NumberFormatValues,
} from 'react-number-format';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { FILTER_FORMAT_MAP } from '../../engine/filterRegistry';
import { FilterId } from '../../types/filters';

interface OperatorOption {
  value: 'between' | 'greater_than' | 'less_than';
  label: string;
}

interface SubFilterInputFormProps {
  filterId: FilterId;
  startValue: number | undefined;
  setStartValue: Dispatch<SetStateAction<number | undefined>>;
  endValue: number | undefined;
  setEndValue: Dispatch<SetStateAction<number | undefined>>;
  operator: OperatorOption;
  setOperator: (operator: OperatorOption) => void;
  errorMessages?: string[];
  showErrors?: boolean;
  onShowErrorsChange?: (show: boolean) => void;
}

const operatorOptions: OperatorOption[] = [
  { value: 'between', label: 'is between' },
  { value: 'greater_than', label: 'is greater than' },
  { value: 'less_than', label: 'is less than' },
];

export const SubFilterInputForm = ({
  filterId,
  startValue,
  setStartValue,
  endValue,
  setEndValue,
  operator,
  setOperator,
  errorMessages = [],
  showErrors = false,
  onShowErrorsChange,
}: SubFilterInputFormProps) => {
  const { currencySymbol } = useCurrencyPreference();
  const format = FILTER_FORMAT_MAP[filterId];
  const isFundingFilter =
    filterId === FilterId.LAST_FUNDING_AMOUNT ||
    filterId === FilterId.TOTAL_FUNDING_AMOUNT;

  const sharedNumberFormatProps = useMemo(() => {
    if (
      filterId === FilterId.GRADUATION_YEAR ||
      filterId === FilterId.LAST_FUNDING_YEAR ||
      filterId === FilterId.YEAR_FOUNDED
    ) {
      return {
        thousandSeparator: false,
        format: '####',
        mask: '_',
      };
    }
    const prefix = (() => {
      if (!format || format.type !== 'prefix') return '';
      return format.text === '$' ? currencySymbol : format.text;
    })();
    const suffix = format?.type === 'suffix' ? format.text : '';
    return {
      thousandSeparator: true,
      ...(prefix && { prefix }),
      ...(suffix && { suffix }),
      ...(isFundingFilter && { decimalScale: 2, allowNegative: false }),
    };
  }, [filterId, currencySymbol, format, isFundingFilter]);

  const isInvalid = showErrors && errorMessages.length > 0;

  const handleOperatorChange = (value: SingleValue<OperatorOption>) => {
    if (value) {
      setOperator(value);
    }
    onShowErrorsChange?.(false);
  };

  return (
    <Flex direction="column" gap="3" fontSize="14px">
      <Select
        options={operatorOptions}
        value={operator}
        onChange={handleOperatorChange}
        size="sm"
        selectedOptionColorScheme="green"
        chakraStyles={{
          control: (provider) => ({
            ...provider,
            height: '36px',
          }),
        }}
      />

      <FormControl isInvalid={isInvalid}>
        {operator.value === 'between' ? (
          <Flex
            alignItems="center"
            gap="2.5"
            fontSize="14px"
            justifyContent="space-between"
          >
            <NumberInput
              {...sharedNumberFormatProps}
              value={startValue}
              onValueChange={(values: NumberFormatValues) => {
                setStartValue(values.floatValue ?? undefined);
                onShowErrorsChange?.(false);
              }}
              data-testid="range-num-input-start"
            />
            <Text>to</Text>
            <NumberInput
              {...sharedNumberFormatProps}
              value={endValue}
              onValueChange={(values: NumberFormatValues) => {
                setEndValue(values.floatValue ?? undefined);
                onShowErrorsChange?.(false);
              }}
              data-testid="range-num-input-end"
            />
          </Flex>
        ) : (
          <NumberInput
            key={`single-${operator.value}`}
            {...sharedNumberFormatProps}
            value={operator.value === 'greater_than' ? startValue : endValue}
            onValueChange={
              operator.value === 'greater_than'
                ? (values: NumberFormatValues) => {
                    setStartValue(values.floatValue ?? undefined);
                    onShowErrorsChange?.(false);
                  }
                : (values: NumberFormatValues) => {
                    setEndValue(values.floatValue ?? undefined);
                    onShowErrorsChange?.(false);
                  }
            }
            data-testid={
              operator.value === 'greater_than'
                ? 'range-num-input-start'
                : 'range-num-input-end'
            }
          />
        )}

        <FormErrorMessage>
          <VStack align="start">
            {errorMessages.map((msg, idx) => (
              <Text key={idx} color="red.600">
                {msg}
              </Text>
            ))}
          </VStack>
        </FormErrorMessage>
      </FormControl>
    </Flex>
  );
};

const NumberInput = ({
  value,
  ...restProps
}: NumberFormatPropsBase<typeof Input> & {
  value?: number | string | undefined;
}) => {
  const normalizedValue = value == null ? '' : value;

  return (
    <NumberFormat
      // @ts-expect-error chakra Input typing is not directly compatible with react-number-format customInput
      customInput={Input}
      height="36px"
      fontSize="14px"
      padding="10px"
      borderRadius="2px"
      thousandSeparator
      displayType="input"
      value={normalizedValue}
      {...restProps}
    />
  );
};
